import React, { useState } from 'react';
import {
    Modal, Paper, Box, Typography, TextField, IconButton,
    List, ListItem, ListItemText, Button, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { Role } from '../../../../models/role';
import CloseIcon from '@mui/icons-material/Close';
import PermissionButton from '../../../../components/common/PermissionButton';
import { shouldRenderForbiddenAction } from '../../../../utils/permission-ui';

interface RoleModalProps {
    open: boolean;
    onClose: () => void;
    roles: Role[];
    canCreate: boolean;
    canDelete: boolean;
    refreshRoles: () => void;
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 700,
    height: 600,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
};

const RoleModal: React.FC<RoleModalProps> = ({
    open,
    onClose,
    roles,
    canCreate,
    canDelete,
    refreshRoles,
    showAlert,
    showLoading,
    hideLoading,
}) => {
    const [newRoleName, setNewRoleName] = useState('');
    const showDeleteActions = shouldRenderForbiddenAction(canDelete);

    const handleCreateRole = async () => {
        if (!canCreate) return;

        try {
            showLoading();
            await axios.post('roles', { name: newRoleName });
            showAlert('Perfil creado exitosamente', 'success');
            setNewRoleName('');
            refreshRoles();
            onClose();
        } catch (error) {
            showAlert('Error al crear el perfil', 'error');
        } finally {
            hideLoading();
        }
    };

    const handleDeleteRole = async (id: number) => {
        if (!canDelete) return;

        try {
            showLoading();
            await axios.delete(`roles/${id}`);
            showAlert('Perfil eliminado exitosamente', 'success');
            refreshRoles();
        } catch (error) {
            showAlert('Error al eliminar el perfil', 'error');
        } finally {
            hideLoading();
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Paper sx={modalStyle}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        bgcolor: "primary.main",
                        color: "white",
                    }}
                >
                    <Typography variant="h6">Administrar Perfil</Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{ color: "white" }}
                        aria-label="cerrar"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Scrollable content */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Nombre del perfil"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                    />

                    <Typography variant="subtitle1" mt={2} mb={1}>
                        Perfiles existentes:
                    </Typography>
                    <List>
                        {roles.map((role) => (
                            <ListItem
                                key={role.id}
                                secondaryAction={
                                    showDeleteActions ? (
                                        <Tooltip title={canDelete ? 'Eliminar' : 'Sin permiso para eliminar'}>
                                            <span>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleDeleteRole(role.id)}
                                                    disabled={!canDelete}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    ) : undefined
                                }
                            >
                                <ListItemText primary={role.name} />
                            </ListItem>
                        ))}
                    </List>
                </Box>

                {/* Fixed action buttons */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        p: 2,
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <Button onClick={onClose} sx={{ mr: 1 }}>
                        Cancelar
                    </Button>
                    <PermissionButton
                        allowed={canCreate}
                        deniedTooltip="Sin permiso para crear"
                        tooltip="Crear"
                        variant="contained"
                        onClick={handleCreateRole}
                    >
                        Crear
                    </PermissionButton>
                </Box>
            </Paper>
        </Modal>

    );
};

export default RoleModal;
